import axios, { AxiosRequestConfig, AxiosInstance } from 'axios'
import env from '../../../config/env'
import { publicPaths } from '../../../routers/urls'
import TenantService from '../../../services/TenantService'
import { writeTokenActions } from '../TokenHelper'

declare const window: Window & { sourceState?: string; sourceStateV2?: string }

const isPublicPath = (url: string) => publicPaths.some(path => url.includes(path))
const isCarrierApp = window?.sourceState === 'carrier-app'

const accessTokenIsNotValid = () => {
  const accessTokenExp = Number(localStorage.getItem('yld_a_token_timeout'))
  const now = Math.round(+new Date() / 1000)
  return now > accessTokenExp
}

const instance: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  headers: {
    'content-type': 'application/json',
    'x-api-key': env.APIKey,
  },
})

instance.interceptors.request.use(
  config => {
    return new Promise(async (resolve, reject) => {
      try {
        if (isPublicPath(config.baseURL || '') || isPublicPath(config.url || '')) {
          const { authorization, ...rest } = config.headers
          config.headers = {
            ...rest,
          }
        }

        if (typeof localStorage.getItem('yld_v') === 'string') {
          config.headers = {
            ...config.headers,
            'x-carrier-web-version': localStorage.getItem('yld_v'),
          }
        }

        if (
          typeof localStorage.getItem('yld_i_token') === 'string' &&
          !isPublicPath(config.baseURL || '') &&
          !isPublicPath(config.url || '')
        ) {
          config.headers = {
            ...config.headers,
            authorization: localStorage.getItem('yld_i_token'),
          }
        }

        if (config.data instanceof FormData) {
          config.headers = {
            ...config.headers,
            'content-type': 'application/x-www-form-urlencoded',
          }
        }

        if (!!config.data?.captchaToken) {
          config.headers = {
            ...config.headers,
            'cf-turnstile-response': config.data?.captchaToken,
          }

          delete config.data?.captchaToken
        }

        if (
          !isPublicPath(config.baseURL || '') &&
          !isPublicPath(config.url || '') &&
          accessTokenIsNotValid()
        ) {
          const res = await TenantService.CreateRefreshToken()

          const result = await res.json()
          writeTokenActions({ accessToken: result?.data?.accessToken, idToken: result?.data?.idToken })
          config.headers.authorization = result?.data?.idToken || ''
        }

        if (isCarrierApp) {
          config.headers = {
            ...config.headers,
            'x-api-key': env.carrierMobileWebViewAPIKey,
          }
        }

        resolve(config)
      } catch (error) {
        reject(error)
      }
    })
  },
  error => {
    return Promise.reject(error)
  }
)

instance.interceptors.response.use(
  response => response,
  error => Promise.reject(error)
)

const fetcher = async ({ baseURL, data = null, method = 'GET', url = '' }: AxiosRequestConfig) => {
  const response = await instance.request({ baseURL, data, method, url })

  return response.data
}

export default fetcher
